/**
 * Posicionamento de balão flutuante com detecção de colisão (I6).
 *
 * Função PURA — recebe retângulos, devolve coordenadas — para ser testada sem navegador e
 * reaproveitada por qualquer flutuante do design system. Faz o que o `@floating-ui` chama de
 * `flip` + `shift`, sem trazer a dependência:
 *
 *  1. tenta o lado pedido; se não cabe, o oposto; depois os perpendiculares;
 *  2. se nenhum cabe, fica com o lado que tem MAIS espaço;
 *  3. no eixo cruzado, empurra o balão para dentro da janela (respeitando `padding`).
 */

export type FloatingSide = "top" | "bottom" | "left" | "right";

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface FloatingPositionInput {
  anchor: Rect;
  floating: Size;
  viewport: Size;
  side: FloatingSide;
  /** Distância entre o gatilho e o balão. */
  offset?: number;
  /** Margem mínima até a borda da janela. */
  padding?: number;
}

export interface FloatingPosition {
  side: FloatingSide;
  top: number;
  left: number;
}

const OPPOSITE: Record<FloatingSide, FloatingSide> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

const PERPENDICULAR: Record<FloatingSide, [FloatingSide, FloatingSide]> = {
  top: ["right", "left"],
  bottom: ["right", "left"],
  left: ["top", "bottom"],
  right: ["top", "bottom"],
};

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/** Espaço livre (pode ser negativo) do lado `side`, já descontando balão, offset e padding. */
function room(side: FloatingSide, input: Required<FloatingPositionInput>): number {
  const { anchor, floating, viewport, offset, padding } = input;
  switch (side) {
    case "top":
      return anchor.top - offset - floating.height - padding;
    case "bottom":
      return viewport.height - (anchor.top + anchor.height) - offset - floating.height - padding;
    case "left":
      return anchor.left - offset - floating.width - padding;
    case "right":
      return viewport.width - (anchor.left + anchor.width) - offset - floating.width - padding;
  }
}

export function computeFloatingPosition(input: FloatingPositionInput): FloatingPosition {
  const full: Required<FloatingPositionInput> = { offset: 8, padding: 8, ...input };
  const { anchor, floating, viewport, offset, padding } = full;

  const order: FloatingSide[] = [input.side, OPPOSITE[input.side], ...PERPENDICULAR[input.side]];
  let side = order.find((candidate) => room(candidate, full) >= 0);
  if (side === undefined) {
    side = order.reduce((best, candidate) =>
      room(candidate, full) > room(best, full) ? candidate : best,
    );
  }

  const centerX = anchor.left + anchor.width / 2;
  const centerY = anchor.top + anchor.height / 2;
  const maxLeft = viewport.width - padding - floating.width;
  const maxTop = viewport.height - padding - floating.height;

  if (side === "top" || side === "bottom") {
    const top =
      side === "top" ? anchor.top - offset - floating.height : anchor.top + anchor.height + offset;
    return {
      side,
      top: clamp(top, padding, maxTop),
      left: clamp(centerX - floating.width / 2, padding, maxLeft),
    };
  }

  const left =
    side === "left" ? anchor.left - offset - floating.width : anchor.left + anchor.width + offset;
  return {
    side,
    top: clamp(centerY - floating.height / 2, padding, maxTop),
    left: clamp(left, padding, maxLeft),
  };
}
